/**
 * Session Management Context — SPA Architecture
 *
 * Manages user session entirely client-side:
 * 1. On mount: calls POST /auth/refresh to restore the session from the httpOnly cookie.
 * 2. Decodes the returned access token (JWT) to extract the session payload.
 * 3. Stores the access token in-memory via auth-fetch (never localStorage).
 * 4. Fetches the user profile after session is established.
 */

"use client"

import { Session } from "@/shared/types/session.type"
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from "react"
import { Profile } from "../types/auth-actions.type"
import { UserRole } from "@/shared/constants/user-roles"
import { setAccessToken, clearAccessToken, authFetchJson } from "@/shared/lib/auth-fetch"
import { getProfile, getVolunteerProfile } from "@/features/profile/api/profile.api"

const BASE_URL = (import.meta.env.VITE_BASE_BACKEND_URL || '/api') as string

/**
 * Decode JWT payload without verification (the server already verified it).
 * We only need the claims for UI routing / display.
 */
function decodeJwtPayload(token: string): Session | null {
  try {
    const parts = token.split(".")
    if (parts.length !== 3) return null
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")))
    return payload as Session
  } catch {
    return null
  }
}

type SessionContextType = {
  session: Session | null
  setSession: (session: Session | null) => void
  profile: Profile | null
  setProfile: (profile: Profile | null) => void
  refreshSession: () => Promise<void>
  logout: () => Promise<void>
  isLoading: boolean
}

export const SessionContext = createContext<SessionContextType | null>(null)

export const SessionProvider = ({ children }: PropsWithChildren) => {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const initSession = useCallback(async () => {
    try {
      setIsLoading(true)

      // Attempt silent refresh — the httpOnly cookie is sent automatically
      const res = await fetch(`${BASE_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      })

      if (!res.ok) {
        // No valid session — user is not logged in
        setSession(null)
        setProfile(null)
        setIsLoading(false)
        return
      }

      const data = await res.json()
      const accessToken: string | undefined = data?.data?.accessToken
      if (!accessToken) {
        setSession(null)
        setProfile(null)
        setIsLoading(false)
        return
      }

      // Store the token in-memory for authFetch
      setAccessToken(accessToken)

      // Decode the JWT to get session claims
      const decoded = decodeJwtPayload(accessToken)
      if (!decoded) {
        setSession(null)
        setProfile(null)
        setIsLoading(false)
        return
      }

      setSession(decoded)
      setIsLoading(false)

      // Fetch profile in background (non-blocking to avoid delaying UI)
      try {
        const profileRes =
          decoded.role === UserRole.HOMEOWNER
            ? await getProfile(Number(decoded.sub))
            : await getVolunteerProfile(Number(decoded.sub))

        if (profileRes.success && profileRes.data) {
          setProfile(profileRes.data)
        }
      } catch {
        // Profile fetch failure is non-fatal
      }
    } catch {
      setSession(null)
      setProfile(null)
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await fetch(`${BASE_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      })
    } catch {
      // Non-fatal
    }
    clearAccessToken()

    // Clear client-side cookies
    document.cookie = "aim-auth=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT"
    document.cookie = "refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT"
    document.cookie = "auth_refresh_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT"

    // Clear local storage auth-related items
    localStorage.removeItem("refreshToken")
    localStorage.removeItem("aim-auth")
    localStorage.removeItem("googleSignupInfo")
    localStorage.removeItem("googleLoginFromLoginPage")
    localStorage.removeItem("selectedGoogleAccountEmail")
    localStorage.removeItem("reloadedAfterLogin")

    setSession(null)
    setProfile(null)
    // Let the AuthGuard handle the redirect to login
  }, [])

  useEffect(() => {
    initSession()
  }, [initSession])

  return (
    <SessionContext.Provider
      value={{
        session,
        setSession,
        profile,
        setProfile,
        refreshSession: initSession,
        logout,
        isLoading,
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}

export const useSession = () => {
  const context = useContext(SessionContext)
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider")
  }
  return context
}